<template>
    <form class="max-w-sm mx-auto">
        <p
            v-if="showErrors && errorMessage && errorMessage.length"
            v-html="errorMessage"
            class="text-white text-center text-xs tablet:text-sm bg-salmon p-4 mb-4"
        >
        </p>
        <label
            v-if="inputType === 'noBorderWithMagnifyingGlass'"
            class="block"
        >
            <input type="text" placeholder="ex. 12345" maxlength="5" tabindex="1" class="my-auto bg-white py-4 rounded block w-full px-4 shadow-sm border-0 focus:ring-0">
        </label>
        <label
            v-else
            class="w-auto"
        >
            ZIP Code

            <input
                v-model.trim="postalCode"
                :class="{ 'border-salmon': $v.postalCode.$error }"
                class="block w-full"
                maxlength="5"
                placeholder="ex. 12345"
                type="text"
            >

            <div v-if="$v.postalCode.$error" class="text-salmon text-sm mt-2">
                <IconCircleExclamation class="inline mr-1" />
                Please enter a valid ZIP Code
            </div>
        </label>

        <label
            v-else
            class="relative flex tablet:w-1/2 w-full flex-wrap items-stretch text-gray-500 mb-3 mx-auto"
        >
            <input
                @keydown.enter.prevent
                v-model.trim="postalCode"
                :class="{ 'border-salmon': $v.formFields.postalCode.$error }"
                type="text"
                placeholder="ex. 12345"
                maxlength="5"
                class="p-3 mx-3 border-0 focus:ring-0 relative bg-white rounded outline-none w-full"
            />
        </label>

        <div class="flex flex-col mt-4">
            <button
                @click.prevent="onSubmit()"
                type="submit"
                tabindex="5"
                id="zip-submit"
                class="btn-primary w-auto mx-auto"
            >
                {{ buttonText }}
            </button>
            <a
                v-if="showFormToggle"
                @click.prevent="$emit('toggle-form-type')"
                class="text-center underline mt-4"
            >
                Check availability by address?
            </a>
        </div>
    </form>
</template>

<script>
import { required, minLength, maxLength, numeric} from 'vuelidate/lib/validators'
import { mapActions, mapState } from "vuex"
import IconCircleExclamation from "@/atoms/icons/IconCircleExclamation"
import IconSearch from "@/atoms/icons/IconSearch"

const DEFAULT_ERROR_MESSAGE = `We're sorry, we are experiencing technical difficulties. Please try again.`,
      NON_SERVICEABLE = `The ZIP Code you entered is not currently serviceable. Please try again.`,
      OUTSIDE_FOOTPRINT = `Your ZIP Code is currently outside of the Breezeline footprint. Check back frequently as we are expanding all the time!`,
      ONLY_RESIDENTIAL = `The ZIP Code you entered is currently only serviceable on the <a class="underline" href="/">residential site</a>`,
      ONLY_BUSINESS = `The ZIP Code you entered is currently only serviceable on the <a class="underline" href="/business">business site</a>`

export default {
    name: 'CheckAvailabilityZip',
    components: {
        IconCircleExclamation,
        IconSearch,
    },
    props: {
        formLayout: {
            type: String,
            default: 'block'
        },
        buttonText: {
            type: String,
            default: 'Continue',
        },
        inputType: {
            type: String,
        },
        showFormToggle:{
            type: Boolean,
            default: false,
        },
        showErrors: {
            type: Boolean,
            default: false
        }
    },

    data: () => ({
        postalCode: null,
        autocompleteHandler: null,
        errorMessage: null,
    }),

    validations: {
        postalCode: {
            required,
            numeric,
            minLength: minLength(5),
            maxLength: maxLength(5),
        },
    },
    computed: {
        ...mapState(['meta']),
    },
    methods: {
        ...mapActions([
            'checkZip',
        ]),
        ...mapActions('Personalization', [
            'ping',
        ]),
        setErrors(error){
            this.errorMessage = error;
            this.$emit('set-errors', error)
        },

        // Dictionary Data Structure that performs actions based on their zip in the CheckAvailability Modal & /business/check-availability page
        checkZipProxy(zipcode) {
            this.setErrors(null);
            this.checkZip(zipcode)
            .then(r => {
                const locationTypes = {
                    'BusinessFiber': {
                        features: ['biz_host_rdirect', 'biz_host_fiber', 'biz_fiber_solution'],
                        redirectPath: '/business/enterprise/fiber-solutions',
                        actions: ['dismiss-modal']
                    },
                    'Business': {
                        features: [],
                        redirectPath: '/business',
                        actions: ['dismiss-modal']
                    },
                    'Residential': {
                        features: [],
                        redirectPath: null,
                        actions: ['dismiss-modal']
                    },
                    'Default': {
                        features: [],
                        redirectPath: null,
                        actions: ['setErrors', 'set-biz-attempt']
                    }
                };

                // Business Fiber: 33133
                // Business non-fiber zip: 43031
                // Residential Only: 44074
                // Default: 00000 / 12345

                function hasBizFiberFeature () {
                    const fiberArray = ['biz_host_rdirect', 'biz_host_fiber', 'biz_fiber_solution'];
                    const responseFeatures = r.location.features;
                    // Has Fiber Feature:
                    return fiberArray.some(feature => responseFeatures.includes(feature))
                }

                function isBusinessPage () {
                    return window.location.pathname.startsWith('/business')
                }

                function hasSitesBusiness () {
                    const responseSites = r.location.sites;
                    return responseSites.includes('Business')
                }

                function hasSitesResidential () {
                    const residentialSites = r.location.sites;
                    return residentialSites.includes('Residential');
                }

                const BusinessFiberCategory = locationTypes.BusinessFiber
                const BusinessNonFiberCategory = locationTypes.Business
                const DefaultCategory = r.location.zipCode.includes('default')

                //BUSINESS FIBER
                if(hasBizFiberFeature() && isBusinessPage()) {
                    locationTypes.BusinessFiber.actions.forEach(action => this.$emit(action));
                    if (BusinessFiberCategory) {
                        if(!window.location.pathname.startsWith('/business/channel-lineup')) {
                            window.location = BusinessFiberCategory.redirectPath;
                        }
                    }
                    return;
                }

                //BUSINESS NON-FIBER
                if(!hasBizFiberFeature() && isBusinessPage() && hasSitesBusiness()) {
                    locationTypes.Business.actions.forEach(action => this.$emit(action));
                    if (BusinessNonFiberCategory) {
                        if(!window.location.pathname.startsWith('/business/channel-lineup')) {
                            window.location = BusinessNonFiberCategory.redirectPath;
                        }
                    }
                    return;
                }

                //RESIDENTIAL ON BUSINESS SITE
                if(isBusinessPage() && hasSitesResidential() && !hasSitesBusiness()){
                    // This error sets the error state on the page /check-availability
                    this.setErrors(ONLY_RESIDENTIAL)
                    return;
                }

                //RESIDENTIAL SITE
                if(hasSitesResidential()){
                    locationTypes.Residential.actions.forEach(action => this.$emit(action));
                    return;
                }

                //DEFAULT
                if(DefaultCategory){
                    locationTypes.Default.actions.forEach(action => this.$emit(action));
                    // This error sets the error state on the page /check-availability
                    console.error('Default Category')
                    this.setErrors(OUTSIDE_FOOTPRINT)
                    return;
                }

            })

                .then(() => {
                    this.ping({
                        eventName: 'CHECK_AVAILABILITY',
                        payload: {
                            user: {
                                attributes: {
                                    zip: this.$store.state.meta.postalCode,
                                    country: 'USA',
                                },
                            },
                        },
                    })
                })
                .catch(error => {
                    if (error.message){
                        console.error(error.message)
                        this.setErrors(OUTSIDE_FOOTPRINT)
                    } else {
                        this.setErrors(OUTSIDE_FOOTPRINT)
                    }
                })
        },
        onSubmit() {
            this.$v.$touch()

            if (!this.$v.$invalid) {
                this.checkZipProxy(this.postalCode)
            }
        },
    },
}
</script>
